<div class="modal-overlay parameter-dialog" mat-dialog-content>
  <div class="parameter-dialog-container">
    <div class="dialog-header">
      <h2 class="dialog-title">
        {{ model?.id && !isAdd ? ('Edit Parameter' | translate) : ('Create Parameter' | translate) }}
        @if (form.controls['isNotificationOnly']?.value) {
          <span class="notif-badge" role="status" aria-label="Notification Only parameter">
            <span aria-hidden="true">●</span>
            {{ 'Notification Only' | translate }}
          </span>
        }
      </h2>
      <button class="dialog-close-btn" (click)="onCancel()" aria-label="Close dialog" type="button">
        <span class="material-icons" aria-hidden="true">close</span>
      </button>
    </div>

    <div class="dialog-body">
      @if (form) {
        <form class="form-grid" [formGroup]="form">
          <div class="form-field full-width">
            <label class="form-label" for="analyteName">
              {{ 'Analyte Name' | translate }}
              <span class="required-mark" aria-hidden="true">*</span>
            </label>
            <input
              id="analyteName"
              type="text"
              class="form-input"
              formControlName="analyteName"
              [ngClass]="{ error: form.controls['analyteName']?.invalid && (form.controls['analyteName']?.dirty || form.controls['analyteName']?.touched) }"
              [disabled]="readonly"
              [attr.aria-disabled]="readonly"
              [attr.aria-required]="true"
              [attr.aria-describedby]="'analyteName-hint'"
              />
              <span id="analyteName-hint" class="form-field-hint">
                {{ 'Analyte Name hint' | translate }}
              </span>
              @if (form.controls['analyteName']?.hasError('required') && (form.controls['analyteName']?.dirty || form.controls['analyteName']?.touched)) {
                <span class="form-error-msg">
                  {{ 'This field is required' | translate }}
                </span>
              }
            </div>
            <div class="form-field">
              <label class="form-label" for="type">
                {{ 'Sample Type' | translate }}
                <span class="required-mark" aria-hidden="true">*</span>
              </label>
              <select
                id="type"
                class="form-select"
                formControlName="type"
                [ngClass]="{ error: form.controls['type']?.invalid && (form.controls['type']?.dirty || form.controls['type']?.touched) }"
                [disabled]="readonly"
                [attr.aria-disabled]="readonly"
                [attr.aria-required]="true"
                >
                <option [ngValue]="null" disabled>{{ 'Select sample type' | translate }}</option>
                @for (type of sampleTypeOptions; track type) {
                  <option [ngValue]="type.id">
                    {{ type.name | translate }}
                  </option>
                }
              </select>
            </div>
            <div class="form-field">
              <div class="form-label-with-pill">
                <label class="form-label form-label--flush" for="parameterType">
                  {{ 'Parameter Type' | translate }}
                  <span class="required-mark" aria-hidden="true">*</span>
                </label>
                <span class="unit-pill" [attr.aria-label]="'Unit: ' + currentUnit">
                  {{ currentUnit }}
                </span>
              </div>
              <select
                id="parameterType"
                class="form-select form-select--pilled"
                formControlName="parameterType"
                [ngClass]="{ error: form.controls['parameterType']?.invalid && (form.controls['parameterType']?.dirty || form.controls['parameterType']?.touched) }"
                [disabled]="readonly"
                [attr.aria-disabled]="readonly"
                [attr.aria-required]="true"
                (change)="onParameterTypeChange()"
                >
                <option [ngValue]="null" disabled>{{ 'Select parameter type' | translate }}</option>
                @for (type of parameterTypeOptions; track type) {
                  <option [ngValue]="type.id">
                    {{ type.name }}
                  </option>
                }
              </select>
              <span class="form-field-hint">
                {{ 'Unit is derived automatically from Parameter Type' | translate }}
              </span>
            </div>
            <div class="form-field">
              <label class="form-label" for="averagingType">
                {{ 'Averaging Type' | translate }}
                <span class="required-mark" aria-hidden="true">*</span>
              </label>
              <select
                id="averagingType"
                class="form-select"
                formControlName="averagingType"
                [ngClass]="{ error: form.controls['averagingType']?.invalid && (form.controls['averagingType']?.dirty || form.controls['averagingType']?.touched) }"
                [disabled]="readonly"
                [attr.aria-disabled]="readonly"
                [attr.aria-required]="true"
                (change)="onAveragingTypeChange()"
                >
                <option [ngValue]="null" disabled>{{ 'Select averaging type' | translate }}</option>
                @for (type of averagingTypeOptions; track type) {
                  <option [ngValue]="type.id">
                    {{ type.name | translate }}
                  </option>
                }
              </select>
              @if (form.controls['averagingType']?.hasError('required') && (form.controls['averagingType']?.dirty || form.controls['averagingType']?.touched)) {
                <span class="form-error-msg">
                  {{ 'Averaging Type is required.' | translate }}
                </span>
              }
            </div>
            <div
              class="form-field conditional-field"
              [ngClass]="{ hidden: form.controls['averagingType']?.value === averagingTypeEnum.None }"
              id="averaging-time-field"
              aria-live="polite"
              >
              <label class="form-label" for="averagingTimeSeconds">
                {{ 'Averaging Time (minutes)' | translate }}
                <span class="required-mark" aria-hidden="true">*</span>
              </label>
              <input
                id="averagingTimeSeconds"
                type="number"
                class="form-input"
                formControlName="averagingTimeSeconds"
                [ngClass]="{ error: form.controls['averagingTimeSeconds']?.invalid && (form.controls['averagingTimeSeconds']?.dirty || form.controls['averagingTimeSeconds']?.touched) }"
                [disabled]="readonly"
                [attr.aria-disabled]="readonly"
                min="0"
                max="1440"
                step="1"
                [attr.aria-required]="form.controls['averagingType']?.value !== averagingTypeEnum.None"
                [attr.aria-describedby]="'averagingTime-hint'"
                />
                <span id="averagingTime-hint" class="form-field-hint">
                  {{ 'Required when Averaging Type is Rolling or Fixed' | translate }}
                </span>
                @if ((form.controls['averagingTimeSeconds']?.hasError('required') || form.controls['averagingTimeSeconds']?.hasError('min')) && (form.controls['averagingTimeSeconds']?.dirty || form.controls['averagingTimeSeconds']?.touched)) {
                  <span class="form-error-msg">
                    {{ 'Averaging Time must be 0 or greater minutes.' | translate }}
                  </span>
                }
              </div>
              <div class="form-field">
                <label class="form-label" for="measurementMode">
                  {{ 'Measurement Mode' | translate }}
                  <span class="required-mark" aria-hidden="true">*</span>
                </label>
                <select
                  id="measurementMode"
                  class="form-select"
                  formControlName="measurementMode"
                  [ngClass]="{ error: form.controls['measurementMode']?.invalid && (form.controls['measurementMode']?.dirty || form.controls['measurementMode']?.touched) }"
                  [disabled]="readonly"
                  [attr.aria-disabled]="readonly"
                  [attr.aria-required]="true"
                  >
                  @for (mode of measurementModeOptions; track mode) {
                    <option [ngValue]="mode.id">
                      {{ mode.name | translate }}
                    </option>
                  }
                </select>
              </div>
              <div class="form-section-label" aria-hidden="true">{{ 'Scope & Access' | translate }}</div>
              <div class="form-field full-width">
                <label class="form-label" for="organizationLevelId">
                  {{ 'Visibility (Org Level)' | translate }}
                  <span class="required-mark" aria-hidden="true">*</span>
                </label>
                <div class="org-level-select" [class.readonly]="readonly">
                  <app-org-tree-select
                    [nodes]="orgLevelOptions"
                    [selectedId]="$safeNavigationMigration(form.get('organizationLevelId')?.value)"
                    [placeholder]="'Select organization level'"
                    [hideNullOption]="true"
                    (selectionChange)="onOrganizationLevelSelectionChange($event)">
                  </app-org-tree-select>
                </div>
                @if (form.controls['organizationLevelId']?.hasError('required') && (form.controls['organizationLevelId']?.dirty || form.controls['organizationLevelId']?.touched)) {
                  <span class="form-error-msg">
                    {{ 'This field is required' | translate }}
                  </span>
                }
                <span class="form-field-hint">
                  {{ 'Parameters set at a higher org level are inherited by child organizations' | translate }}
                </span>
              </div>
              <div class="form-section-label" aria-hidden="true">{{ 'Alerts & Classification' | translate }}</div>
              <div class="form-field full-width">
                <label class="form-label" id="notif-only-label">{{ 'Notification Only' | translate }}</label>
                <div class="toggle-field" [attr.aria-labelledby]="'notif-only-label'">
                  <label class="toggle-switch" [attr.aria-label]="'Notification Only toggle'">
                    <input
                      type="checkbox"
                      formControlName="isNotificationOnly"
                      [disabled]="readonly"
                      [attr.aria-disabled]="readonly"
                      [attr.aria-describedby]="'notif-only-helper'"
                      />
                      <span class="toggle-track" aria-hidden="true"></span>
                    </label>
                    <div class="toggle-label-group">
                      <div class="toggle-label-title">{{ 'Notification Only' | translate }}</div>
                      <div class="toggle-helper" id="notif-only-helper">
                        {{ 'Notification Only description' | translate }}
                      </div>
                    </div>
                  </div>
                </div>
                <div class="form-section-label" aria-hidden="true">{{ 'Thresholds & Color Coding' | translate }}</div>
                <div class="form-field full-width">
                  @if (model) {
                    <app-parameter-threshold-editor
                      [parameterId]="model.id"
                      [readonly]="readonly"
                      (saved)="onThresholdsSaved($event)"
                      aria-label="Threshold bands editor"
                    ></app-parameter-threshold-editor>
                  }
                </div>
                <div class="form-section-label" aria-hidden="true">
                  {{ 'Clinical Context' | translate }}
                  <span class="form-section-label__optional">
                    ({{ 'optional' | translate }})
                  </span>
                </div>
                <div class="form-field full-width">
                  <label class="form-label" for="healthAdvisory">{{ 'Health Advisory' | translate }}</label>
                  <textarea
                    id="healthAdvisory"
                    class="form-textarea"
                    formControlName="healthAdvisory"
                    [disabled]="readonly"
                    [attr.aria-disabled]="readonly"
                    [attr.aria-describedby]="'health-advisory-hint'"
                  ></textarea>
                  <span id="health-advisory-hint" class="form-field-hint">
                    {{ 'Health Advisory description' | translate }}
                  </span>
                </div>
                <div class="form-field full-width">
                  <label class="form-label" for="specificRecommendations">{{ 'Specific Recommendations' | translate }}</label>
                  <textarea
                    id="specificRecommendations"
                    class="form-textarea"
                    formControlName="specificRecommendations"
                    [disabled]="readonly"
                    [attr.aria-disabled]="readonly"
                    [attr.aria-describedby]="'specific-recs-hint'"
                  ></textarea>
                  <span id="specific-recs-hint" class="form-field-hint">
                    {{ 'Specific Recommendations description' | translate }}
                  </span>
                </div>
              </form>
            }
          </div>

          <div class="dialog-footer" mat-dialog-actions>
            <button
              type="button"
              class="btn btn-cancel"
              (click)="onCancel()"
              [attr.aria-label]="'Cancel'"
              >
              {{ 'Cancel' | translate }}
            </button>
            <button
              type="button"
              class="btn btn-primary"
              (click)="save()"
              [disabled]="!form || form.invalid || isSaving || readonly"
              [attr.aria-label]="'Save Parameter'"
              >
              {{ 'Save' | translate }}
            </button>
          </div>
        </div>
      </div>

